import { useCallback, useMemo } from "react";

import { useHistory, useLocation } from "react-router-dom";

import { deleteNil, parseQuery, stringifyQuery } from "@/utils";

/* Companion to the hooks in react-router-dom, allowing getting query parameters using
 * a regular Object.

 * This function will not be necessary past react-router-dom 6.4.0 where "useSearchParams" is available.
 */

type SetQuery<T extends Record<string, unknown>> = (
  nq: Partial<T>,
  clear?: boolean
) => void;

export type UseSearchParamType<T extends Record<string, unknown>> = [
  T,
  SetQuery<T>,
];

function useSearchParams<
  T extends Record<string, unknown>,
>(): UseSearchParamType<T> {
  const { search } = useLocation();
  const history = useHistory();

  const query = useMemo(() => parseQuery<T>(search), [search]);

  const setQuery = useCallback<SetQuery<T>>(
    (nextQuery: Partial<T> = {}, clear = false) => {
      const q: T = clear ? (nextQuery as T) : ({ ...query, ...nextQuery } as T);
      history.push({
        search: stringifyQuery(deleteNil(q)),
      });
    },
    [query, history]
  );

  return [query, setQuery];
}

export default useSearchParams;
